import React, { useMemo, useState } from "react";

import { DISTRICTS, districtOfWord } from "../../lib/tokiPonaGarden";
import { ROLES } from "../../lib/tokiPonaRoles";
import { analyzeSentence, dealSprint, targetRole } from "../../lib/tokiPonaSprint";

/* ============================================================
   kon nimi — the len section's chip sentence-builder. Tap chips
   to build a toki pona sentence and get live feedback on which
   hat the target word ends up wearing.
   ============================================================ */

// Dependable helpers that make almost any sentence buildable, on top of
// the word's own district neighbors.
const HELPER_POOL = [
  "mi", "sina", "ona", "jan", "ni", "pona", "suli", "mute",
  "lukin", "tomo", "moku", "tenpo",
];

let tileSeq = 0;
const nextTileId = () => {
  tileSeq += 1;
  return `t${tileSeq}`;
};

const HatBuilder = ({ word, hat, initialTokens = [], onSave }) => {
  const deal = useMemo(() => {
    const district = DISTRICTS.find((d) => d.id === districtOfWord(word));
    const districtWords = district ? district.words : [];
    const available = [...new Set([...districtWords, ...HELPER_POOL])];
    return dealSprint(word, available);
  }, [word]);

  const [tiles, setTiles] = useState(() =>
    initialTokens.map((tok) => ({ id: nextTileId(), tok }))
  );

  const addTile = (tok) => setTiles((t) => [...t, { id: nextTileId(), tok }]);
  const removeTile = (id) => setTiles((t) => t.filter((tile) => tile.id !== id));
  const clear = () => setTiles([]);

  const tokens = tiles.map((t) => t.tok);
  const analysis = tokens.length ? analyzeSentence(tokens) : null;

  let hint = `tap chips to build a sentence with ${word}`;
  let hintClass = "";
  let canSave = false;

  if (analysis) {
    if (!analysis.ok) {
      hint = analysis.error;
      hintClass = "bad";
    } else {
      const { found, role } = targetRole(analysis, word);
      if (found === 0) {
        hint = `this sentence doesn't use ${word} yet`;
      } else if (found > 1) {
        hint = `${word} shows up more than once — use it just one time`;
      } else {
        const roleLabel = (ROLES[role] && ROLES[role].label) || role;
        const hit = role === hat;
        hint = `right now ${word} is wearing the ${roleLabel} hat${hit ? " ✓" : ""}`;
        hintClass = hit ? "hit" : "";
        canSave = hit;
      }
    }
  }

  const save = () => {
    if (!canSave) return;
    onSave(tokens.join(" "));
  };

  return (
    <div className="kn-build">
      <div className="kn-build-row">
        {tiles.map((tile) => (
          <button
            type="button"
            key={tile.id}
            className={`kn-chip${tile.tok === word ? " kn-chip-target" : ""}`}
            onClick={() => removeTile(tile.id)}
          >
            {tile.tok}
          </button>
        ))}
      </div>

      <p className={`kn-build-hint${hintClass ? ` ${hintClass}` : ""}`}>{hint}</p>

      <div className="kn-build-bank">
        <span className="kn-build-bank-label">the word</span>
        <div className="kn-build-bank-row">
          <button
            type="button"
            className="kn-chip kn-chip-target"
            onClick={() => addTile(deal.word)}
          >
            {deal.word}
          </button>
        </div>

        <span className="kn-build-bank-label">little words</span>
        <div className="kn-build-bank-row">
          {deal.particles.map((p) => (
            <button type="button" key={p} className="kn-chip" onClick={() => addTile(p)}>
              {p}
            </button>
          ))}
        </div>

        <span className="kn-build-bank-label">helpers</span>
        <div className="kn-build-bank-row">
          {deal.helpers.map((h) => (
            <button type="button" key={h} className="kn-chip" onClick={() => addTile(h)}>
              {h}
            </button>
          ))}
        </div>
      </div>

      <div className="kn-build-actions">
        <button type="button" className="kn-edit" onClick={clear}>
          clear
        </button>
        <button type="button" className="kn-save" disabled={!canSave} onClick={save}>
          save
        </button>
      </div>
    </div>
  );
};

export default HatBuilder;
